import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import LanguageContext from "components/LanguageContext";
import Wrapper from "components/LayoutFront/Wrapper";
import SearchBar from "components/templates/comedec/SearchBar";
import Button from "components/templatesComponents/Button";
import InfoBlock from "components/templatesComponents/InfoBlock";
import Link from "components/templatesComponents/Link";
import SelectDSFR from "components/templatesComponents/SelectDSFR";
import { buttonStyled } from "components/templatesComponents/StyledComponent";
import PropTypes from "prop-types";
import React, { useContext, useEffect, useMemo, useState } from "react";
import ComedecService from "services/ComedecService";
import t from "utils/locales/translation.json";
import { accentNormalize } from "utils/urlUtils";

const FRANCE = "1";
const ABROAD = "2";

const Comedec = (props) => {
  const { page = {} } = props;

  const { title, shortDescription, contents } = page;
  const { dynamicPart, dynamicPart2 } = contents;

  const { language } = useContext(LanguageContext);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));

  const [country, setCountry] = useState(FRANCE);
  const [department, setDepartment] = useState("");
  const [city, setCity] = useState({});
  const [departments, setDepartments] = useState([]);
  const [returnCode, setReturnCode] = useState("");
  const [messageCityName, setMessageCityName] = useState("");
  const [messageDepartmentName, setMessageDepartmentName] = useState("");
  const [getDepartmentsError, setGetDepartmentsError] = useState(false);

  const { nom: departmentName, code: postalCode } = department || {};

  const { nom: cityName, codeDepartement } = city || {};

  const countryTabs = useMemo(
    () => [
      { key: FRANCE, label: t[language].comedec.france, value: FRANCE },
      { key: ABROAD, label: t[language].comedec.foreign, value: ABROAD },
    ],
    [language],
  );

  const getDepartments = async () => {
    try {
      const newDepartments = await ComedecService.getDepartments();
      setDepartments(newDepartments);
    } catch (e) {
      setGetDepartmentsError(true);
    }
  };

  useEffect(() => {
    getDepartments();
  }, []);

  useEffect(() => {
    if (!getDepartmentsError && codeDepartement) {
      setDepartment(Array.isArray(departments) && departments.find((dep) => dep.code === codeDepartement));
    }
  }, [departments, codeDepartement, getDepartmentsError]);

  const checkComedec = () => {
    if (window && cityName) {
      ComedecService.getCityInfos({ nom: accentNormalize(cityName.toUpperCase()), codeDepartement }).then(
        (response) => {
          // eslint-disable-next-line global-require
          const XMLParser = require("react-xml-parser");
          const json = new XMLParser().parseFromString(response);
          const children = json.children[0].children[0]?.children;
          const code = Array.isArray(children) && children.find((c) => c.name === "code_retour");
          setReturnCode(code ? code.value : "");
          setMessageCityName(cityName);
          setMessageDepartmentName(departmentName);
        },
      );
    }
  };

  const handleDepartmentChange = (e) => {
    const { name: fieldName } = e.target;
    let { value } = e.target;
    if (fieldName === "department") {
      value = departments?.find((dep) => dep.nom === value);
    }
    setDepartment(value);
  };

  let responseMessages;

  switch (returnCode) {
    case "1":
      responseMessages = {
        key: "noDemat",
        icon: { iconDSFR: "error-warning-line" },
        title: `${t[language].comedec.common_message} ${messageCityName}${
          messageDepartmentName ? ` (${messageDepartmentName})` : ""
        } ${t[language].comedec.no_demat_message1}`,
        text: t[language].comedec.no_demat_message2,
      };
      break;
    case "2":
    case "3":
      responseMessages = {
        key: "noDemat",
        icon: { iconDSFR: "success-line" },
        title: `${t[language].comedec.common_message} ${messageCityName}${
          messageDepartmentName ? ` (${messageDepartmentName})` : ""
        } ${t[language].comedec.demat_message1}`,
        text: (
          <Stack spacing={2}>
            <p>{t[language].comedec.demat_message2}</p>
            <Link
              url="https://www.legifrance.gouv.fr/jorf/id/JORFTEXT000023570855"
              external
              sx={{ backgroundImage: "none", textDecoration: "underline" }}
            >
              {t[language].comedec.demat_message3}
            </Link>
          </Stack>
        ),
      };
      break;
    default:
      break;
  }

  return (
    <Wrapper>
      <Stack spacing={4} sx={{ px: { xs: 2, lg: 0 } }}>
        <h1>{title}</h1>
        {shortDescription && <p className="fr-text--xl">{shortDescription}</p>}
        {dynamicPart}
        <fieldset className="fr-segmented">
          <legend className={`fr-segmented__legend${isMobile ? "" : " fr-segmented__legend--inline"}`}>
            {t[language].comedec.country_label} :
          </legend>
          <div className="fr-segmented__elements">
            {countryTabs.map((tab) => (
              <div className="fr-segmented__element" key={tab.key}>
                <input
                  value={tab.value}
                  checked={country === tab.value}
                  type="radio"
                  id={`segmented-1-${tab.key}`}
                  name="country"
                  onChange={(e) => setCountry(e.target.value)}
                />
                <label className="fr-label" htmlFor={`segmented-1-${tab.key}`}>
                  {tab.label}
                </label>
              </div>
            ))}
          </div>
        </fieldset>
        {country === FRANCE && (
          <>
            <Box component="p" sx={{ color: "var(--text-mention-grey)" }}>
              {t[language].common.required_fields_mention}
            </Box>
            <Stack
              direction={{ xs: "column", lg: "row" }}
              spacing={3}
              sx={{ alignItems: { xs: "stretch", lg: "flex-end" } }}
              className="fr-mb-2w"
            >
              <Box className="fr-select-group fr-mb-0" sx={{ flex: 4 }}>
                <label className="fr-label" htmlFor="select-department">
                  {t[language].comedec.department}
                </label>
                <SelectDSFR
                  name="department"
                  id="select-department"
                  value={department?.nom || ""}
                  onChange={handleDepartmentChange}
                >
                  <option value="" selected>
                    {t[language].comedec.department_placeholder}
                  </option>
                  {Array.isArray(departments) &&
                    departments.map((d) => (
                      <option key={d.code} value={d.nom}>
                        {d.code} - {d.nom}
                      </option>
                    ))}
                </SelectDSFR>
              </Box>
              <Box sx={{ flex: 5 }}>
                <SearchBar postalCode={postalCode} codeDepartement={codeDepartement} setCity={setCity} />
              </Box>
              <Button
                iconDSFR="search-line"
                onClick={() => checkComedec()}
                disabled={!cityName || !codeDepartement}
                title={t[language].common.search.button_title}
                className={isMobile ? "fr-mt-5w" : ""}
                sx={{ ...buttonStyled, height: "fit-content" }}
              >
                {t[language].comedec.button_label}
              </Button>
            </Stack>
            {returnCode && (
              <InfoBlock
                {...responseMessages}
                accentColor="default"
                sx={{
                  backgroundImage: "linear-gradient(0deg,var(--border-plain-info),var(--border-plain-info))",
                  "&:before": { color: "var(--border-plain-info)" },
                }}
              />
            )}
          </>
        )}
        {country === ABROAD && (
          <InfoBlock
            icon={{ iconDSFR: "info-line" }}
            text={
              <>
                {t[language].comedec.foreign_message1}
                <Link basic external url="https://www.legifrance.gouv.fr/jorf/id/JORFTEXT000023570855">
                  {t[language].comedec.foreign_link}
                </Link>
                {t[language].comedec.foreign_message2}
              </>
            }
            accentColor="default"
            sx={{
              backgroundImage: "linear-gradient(0deg,var(--border-plain-info),var(--border-plain-info))",
              "&:before": { color: "var(--border-plain-info)" },
            }}
          />
        )}
        {dynamicPart2}
      </Stack>
    </Wrapper>
  );
};

Comedec.propTypes = {
  page: PropTypes.shape(),
};

export default Comedec;
